import axios from "axios";
import api, { ApiResponse } from "./../api";
import {
    TerminalViewPickupSummaryListModel,
    TerminalViewPickupSummaryFilterListModel,
    TerminalViewPickupSummaryTilesModel,
    TerminalViewPickupSummaryDetailModel,
    CustomerViewPickupSummaryTilesModel,
    CustomerViewPickupSummaryFilterListModel,
    CustomerViewPickupSummaryListModel,
} from "./models";
import { PageResponseModel } from "../common/models";
import { XGSSelectOption } from "ui-components/xgs-select/xgsSelect";
import { apiQueryAdapter } from "../apiQueryAdapter";

export const REQUEST_ID: {
    [key: string]: string;
} = {
    GET_PICKUP_SUMMARY: "get_pickup_summary",
    GET_PICKUP_SUMMARY_TILES: "get_pickup_summary_tiles",
    GET_TERMINAL_VIEW_PICKUPSUMMARY_DETAIL: "get_terminal_view_pickupsummary_detail",
    GET_ORIGIN_TERMINALS: "get_origin_terminals",
    GET_DESTINATION_TERMINALS: "get_destination_terminals",
    GET_CUSTOMER_PICKUP_SUMMARY: "get_customer_pickup_summary",
    GET_CUSTOMER_PICKUP_SUMMARY_TILES: "get_customer_pickup_summary_tiles",
    GET_CUSTOMER_VIEW_PICKUPSUMMARY_DETAIL: "get_customer_view_pickupsummary_detail",
    GET_REQUESTED_COMPANY: "get_requested_company",
    GET_DOWNLOAD_TERMINALVIEW: "get_download_terminalview",
    GET_DOWNLOAD_CUSTOMERVIEW: "get_download_customerview",
};

class PickupSummaryService {
    private source: any;
    private static instance: PickupSummaryService;
    private constructor() {
        this.source = {};
    }
    static getInstance(): PickupSummaryService {
        if (!PickupSummaryService.instance) {
            PickupSummaryService.instance = new PickupSummaryService();
        }
        return PickupSummaryService.instance;
    };

    public cancelRequest = (requestId: string) => {
        this.source[requestId] && this.source[requestId].cancel();
    }

    public getPickupSummaryList = async (
        filter: TerminalViewPickupSummaryFilterListModel,
        page?: number
    ): Promise<ApiResponse<PageResponseModel<TerminalViewPickupSummaryListModel>>> => {
        const params = new URLSearchParams();

        for (const property in filter) {
            const value = filter[property as keyof TerminalViewPickupSummaryFilterListModel];
            if (value !== undefined && value !== null && value !== "") {
                params.append(property, String(value));
            }
        }

        if (page !== undefined) {
            params.append("page", page.toString());
        }

        this.cancelRequest(REQUEST_ID.GET_PICKUP_SUMMARY);
        this.source[REQUEST_ID.GET_PICKUP_SUMMARY] = axios.CancelToken.source();

        return await api.get("/pickups/terminal-summary", {
            params,
            cancelToken: this.source[REQUEST_ID.GET_PICKUP_SUMMARY].token
        });
    };

    public getPickupSummaryTiles = async (
        filter: TerminalViewPickupSummaryFilterListModel,
        page?: number
    ): Promise<ApiResponse<TerminalViewPickupSummaryTilesModel>> => {
        const params = new URLSearchParams();
        for (const property in filter) {
            const value = filter[property as keyof TerminalViewPickupSummaryFilterListModel];
            if (value !== undefined && value !== null && value !== "") {
                params.append(property, String(value));
            }
        }

        if (page !== undefined) {
            params.append("page", page.toString());
        }
        this.cancelRequest(REQUEST_ID.GET_PICKUP_SUMMARY_TILES);
        this.source[REQUEST_ID.GET_PICKUP_SUMMARY_TILES] = axios.CancelToken.source();
        return await api.get("/pickups/terminal-summary-tiles", { params, cancelToken: this.source[REQUEST_ID.GET_PICKUP_SUMMARY_TILES].token });
    };

    public fetchOriginTerminals = async (search: string): Promise<XGSSelectOption[]> => {
        const REQUEST_KEY = "GET_ORIGIN_TERMINALS";

        this.cancelRequest(REQUEST_KEY);
        this.source[REQUEST_KEY] = axios.CancelToken.source();

        try {
            const response = await api.get<any[]>("/terminals/origin-terminal", {
                params: { search },
                cancelToken: this.source[REQUEST_KEY].token,
            });

            return response.data.map((t) => ({
                label: t.terminalName,
                value: t.terminalNumber,
            }));
        } catch (error: any) {
            if (axios.isCancel(error)) {
                console.log("Request cancelled:", REQUEST_KEY);
                return [];
            }
            console.error("Failed to fetch origin terminals:", error);
            return [];
        }
    };

    public fetchDestinationTerminals = async (search: string): Promise<XGSSelectOption[]> => {
        const REQUEST_KEY = "GET_DESTINATION_TERMINALS";

        this.cancelRequest(REQUEST_KEY);
        this.source[REQUEST_KEY] = axios.CancelToken.source();

        try {
            const response = await api.get<any[]>("/terminals/destination-terminal", {
                params: { search },
                cancelToken: this.source[REQUEST_KEY].token,
            });

            return response.data.map((t) => ({
                label: t.terminalName,
                value: t.terminalNumber,
            }));
        } catch (error: any) {
            if (axios.isCancel(error)) {
                console.log("Request cancelled:", REQUEST_KEY);
                return [];
            }
            console.error("Failed to fetch destination terminals:", error);
            return [];
        }
    };

    public getTerminalViewPickupSummaryList = async (
        terminalNumber: number,
        filter?: TerminalViewPickupSummaryFilterListModel,
        page?: number
    ): Promise<ApiResponse<PageResponseModel<TerminalViewPickupSummaryDetailModel>>> => {
        const params = new URLSearchParams();

        for (const property in filter) {
            const value = filter[property as keyof TerminalViewPickupSummaryFilterListModel];
            if (value !== undefined && value !== null && value !== "") {
                params.append(property, String(value));
            }
        }

        if (page !== undefined) {
            params.append("page", page.toString());
        }

        this.cancelRequest(REQUEST_ID.GET_TERMINAL_VIEW_PICKUPSUMMARY_DETAIL);
        this.source[REQUEST_ID.GET_TERMINAL_VIEW_PICKUPSUMMARY_DETAIL] = axios.CancelToken.source();

        return await api.get(`/pickups/terminal-summary/${terminalNumber}/terminal-events`, {
            params,
            cancelToken: this.source[REQUEST_ID.GET_TERMINAL_VIEW_PICKUPSUMMARY_DETAIL].token
        });
    };

    public getCustomerViewPickupSummaryList = async (
        filter: CustomerViewPickupSummaryFilterListModel,
        page?: number
    ): Promise<ApiResponse<PageResponseModel<CustomerViewPickupSummaryListModel>>> => {
        const params = new URLSearchParams();

        for (const property in filter) {
            const value = filter[property as keyof CustomerViewPickupSummaryFilterListModel];
            if (value !== undefined && value !== null && value !== "") {
                params.append(property, String(value));
            }
        }

        if (page !== undefined) {
            params.append("page", page.toString());
        }

        return await apiQueryAdapter.query(
            "/pickups/customer-summary",
            "GET",
            { params }
        );
    };

    public getCustomerViewPickupSummaryTiles = async (
        filter: CustomerViewPickupSummaryFilterListModel,
        page?: number
    ): Promise<ApiResponse<CustomerViewPickupSummaryTilesModel>> => {
        const params = new URLSearchParams();
        for (const property in filter) {
            const value = filter[property as keyof CustomerViewPickupSummaryFilterListModel];
            if (value !== undefined && value !== null && value !== "") {
                params.append(property, String(value));
            }
        }

        if (page !== undefined) {
            params.append("page", page.toString());
        }
        this.cancelRequest(REQUEST_ID.GET_CUSTOMER_PICKUP_SUMMARY_TILES);
        this.source[REQUEST_ID.GET_CUSTOMER_PICKUP_SUMMARY_TILES] = axios.CancelToken.source();

        return await apiQueryAdapter.query(
            "/pickups/customer-summary-tiles",
            "GET",
            { params }
        );
    };

    public fetchRequesterCompany = async (): Promise<XGSSelectOption[]> => {
        const REQUEST_KEY = "GET_REQUESTED_COMPANY";

        this.cancelRequest(REQUEST_KEY);
        this.source[REQUEST_KEY] = axios.CancelToken.source();

        try {
            const response = await api.get<any[]>("/teams/team-list", {
                cancelToken: this.source[REQUEST_KEY].token,
            });

            return response.data.map((t) => ({
                label: t.name,
                value: t.id,
            }));
        } catch (error: any) {
            if (axios.isCancel(error)) {
                console.log("Request cancelled:", REQUEST_KEY);
                return [];
            }
            console.error("Failed to fetch requested company:", error);
            return [];
        }
    };

    public getCustomerViewPickupDetailList = async (
        terminalNumber: number,
        filter?: CustomerViewPickupSummaryFilterListModel,
        page?: number
    ): Promise<ApiResponse<PageResponseModel<TerminalViewPickupSummaryDetailModel>>> => {
        const params = new URLSearchParams();

        for (const property in filter) {
            const value = filter[property as keyof CustomerViewPickupSummaryFilterListModel];
            if (value !== undefined && value !== null && value !== "") {
                params.append(property, String(value));
            }
        }

        if (page !== undefined) {
            params.append("page", page.toString());
        }

        return await apiQueryAdapter.query(
            `/pickups/customer-summary/${terminalNumber}/customer-events`,
            "GET",
            { params }
        );
    };

    public getDownloadCSVTerminalView = async (
        filter: TerminalViewPickupSummaryFilterListModel,
        page?: number
    ): Promise<Blob> => {
        const params = new URLSearchParams();

        for (const property in filter) {
            const value = filter[property as keyof TerminalViewPickupSummaryFilterListModel];
            if (value !== undefined && value !== null && value !== "") {
                params.append(property, String(value));
            }
        }

        this.cancelRequest(REQUEST_ID.GET_DOWNLOAD_TERMINALVIEW);
        this.source[REQUEST_ID.GET_DOWNLOAD_TERMINALVIEW] = axios.CancelToken.source();

        const response = await api.get("/pickups/terminal-summary/export", {
            params,
            cancelToken: this.source[REQUEST_ID.GET_DOWNLOAD_TERMINALVIEW].token,
            responseType: "blob",
        });

        return response.data;
    };

    public getDownloadCSVCustomerView = async (
        filter: CustomerViewPickupSummaryFilterListModel
    ): Promise<Blob> => {
        const params = new URLSearchParams();

        for (const property in filter) {
            const value = filter[property as keyof CustomerViewPickupSummaryFilterListModel];
            if (value !== undefined && value !== null && value !== "") {
                params.append(property, String(value));
            }
        }

        this.cancelRequest(REQUEST_ID.GET_DOWNLOAD_CUSTOMERVIEW);
        this.source[REQUEST_ID.GET_DOWNLOAD_CUSTOMERVIEW] = axios.CancelToken.source();

        const response = await api.get("/pickups/customer-summary/export", {
            params,
            cancelToken: this.source[REQUEST_ID.GET_DOWNLOAD_CUSTOMERVIEW].token,
            responseType: "blob",
        });

        return response.data;
    };

};

export default PickupSummaryService;